import { ReactNode } from 'react'
import * as RadixSelect from '@radix-ui/react-select'
import cn from 'classnames'

import Typography from '~/components/Typography'

import styles from './Select.pcss'

export type Option<T> = {
	label: ReactNode
	disabled?: boolean
	value: T
}

interface Props<T> {
	options: Option<T>[]
	value?: T
	placeholder?: string
	onChange?: (value: Option<T>) => void
	disabled?: boolean
}

function Select<T>({
	placeholder = 'Select option',
	options,
	value,
	onChange = () => {},
	disabled,
}: Props<T>) {
	const currentIndex = options.findIndex((option) => option.value === value)
	const currentOption = options[currentIndex]

	const handleChange = (index: string) => {
		onChange(options[Number(index)])
	}

	return (
		<div className={cn(styles.root, disabled && styles.disabled)}>
			<RadixSelect.Root
				value={currentOption ? String(currentIndex) : ''}
				onValueChange={handleChange}
				disabled={disabled}
			>
				<RadixSelect.Trigger className={styles.select}>
					<RadixSelect.Value
						placeholder={
							<Typography color="gray" nowrap>
								{placeholder}
							</Typography>
						}
					>
						<Typography color="white" nowrap>
							{currentOption?.label}
						</Typography>
					</RadixSelect.Value>
					{!disabled && (
						<RadixSelect.Icon asChild>
							<svg
								fill="none"
								height="24"
								viewBox="0 0 24 24"
								width="24"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="m8 10 4 4 4-4"
									stroke="#fff"
									strokeLinecap="square"
									strokeLinejoin="bevel"
									strokeWidth="1.5"
								/>
							</svg>
						</RadixSelect.Icon>
					)}
				</RadixSelect.Trigger>

				<RadixSelect.Portal>
					<RadixSelect.Content
						className={styles.options}
						position="popper"
						side="bottom"
						align="start"
						sideOffset={0}
					>
						<RadixSelect.Viewport>
							{options.map((item, idx) => (
								<RadixSelect.Item
									className={styles.optionItem}
									key={idx}
									value={String(idx)}
									disabled={item.disabled}
								>
									<RadixSelect.ItemText>
										<Typography color="white" nowrap>
											{item.label}
										</Typography>
									</RadixSelect.ItemText>
								</RadixSelect.Item>
							))}
						</RadixSelect.Viewport>
					</RadixSelect.Content>
				</RadixSelect.Portal>
			</RadixSelect.Root>
		</div>
	)
}

export default Select
